import { useEffect, useMemo, useState, useId } from "react";
import { Link } from "react-router-dom";
import { SectionCard } from "shared/app/SectionCard";
import { CheckComponent } from "shared/app/VerificationTemplate/components/CheckComponent";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import {
  Button,
  ButtonVariant,
  CheckboxCard,
  Hide,
  Show
} from "shared/components";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { Loader } from "shared/components/Loader";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { cx } from "shared/utils";
import { ChevronDown, ChevronUp } from "untitledui-js-base";
import { useVerificationTemplatesController } from "views/verification-templates/controller";

interface ScreeningSettingSelectorProps {
  readOnly?: boolean;
  selectedTemplateId?: string;
  useGroupTemplate?: boolean;
  onTemplateSelect?: (id: string) => void;
  onUseGroupTemplateChange?: (checked: boolean) => void;
  footer?: React.ReactNode;
  headerExtra?: React.ReactNode;
  hasPropertyGroup?: boolean;
  isUpdating?: boolean;
}

export const ScreeningSettingsForm = ({
  readOnly,
  selectedTemplateId,
  useGroupTemplate,
  onTemplateSelect,
  onUseGroupTemplateChange,
  footer,
  headerExtra,
  hasPropertyGroup,
  isUpdating
}: ScreeningSettingSelectorProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const { templates, isFetching } = useVerificationTemplatesController(999);
  const [reviewModalId, setReviewModalId] = useState<string | null>(null);
  const instanceId = useId();

  const sortedTemplates = useMemo(
    () =>
      [...(templates || [])].sort(
        (a, b) => Number(b.default) - Number(a.default)
      ),
    [templates]
  );

  const renderedTemplates = useMemo(
    () => (showMoreSettings ? sortedTemplates : sortedTemplates.slice(0, 7)),
    [showMoreSettings, sortedTemplates, 7]
  );

  useEffect(() => {
    if (renderedTemplates.length && !selectedTemplateId)
      onTemplateSelect?.(renderedTemplates[0].id);
  }, [renderedTemplates, selectedTemplateId, onTemplateSelect]);

  if (isFetching)
    return (
      <SectionCard title="Screening settings">
        <div className="flex items-center justify-center w-[700px]">
          <Loader />
        </div>
      </SectionCard>
    );

  return (
    <SectionCard
      title="Screening settings"
      headerExtra={headerExtra}
      footer={footer}
    >
      <div className="divide-y divide-alpha-40">
        <Show if={hasPropertyGroup}>
          <div className="pb-6">
            {readOnly ? (
              <CheckComponent
                checked={Boolean(useGroupTemplate)}
                label={`Use ${dictionary.property} group template`}
              />
            ) : (
              <Switch
                size={SwitchSize.SM}
                label={`Use ${dictionary.property} group template`}
                checked={Boolean(useGroupTemplate)}
                onChange={(checked) => onUseGroupTemplateChange?.(checked)}
              />
            )}
          </div>
        </Show>

        <Show if={templates && templates.length === 0}>
          <p className="text-sm font-medium text-danger-500 py-6">
            Hm.. Looks like you don&apos;t have any templates. Create one
          </p>
        </Show>
        <div
          className={cx(
            "grid grid-cols-4 gap-4 overflow-y-auto max-h-[322px]",
            hasPropertyGroup ? "pt-6" : "",
            isUpdating ? "opacity-50 pointer-events-none" : ""
          )}
        >
          <Show if={!readOnly}>
            {useGroupTemplate ? (
              <CheckboxCard
                disabled={true}
                id={`${instanceId}-create-template`}
                wrapperClassName="border-primary-500 border-dashed bg-primary-20 rounded-lg cursor-not-allowed"
                label={
                  <p className="w-full text-center text-sm font-medium text-primary-500">
                    Create template
                  </p>
                }
                name="create-template"
                onChange={() => {}}
                className="flex min-h-[76px] w-[176px] flex-col items-center  justify-center gap-2 rounded-lg border-2 border-dashed border-primary-500 bg-primary-20"
              />
            ) : (
              <Link to="/verification-templates?create-mode=true">
                <div>
                  <CheckboxCard
                    disabled={false}
                    id={`${instanceId}-create-template`}
                    wrapperClassName="border-primary-500 border-dashed bg-primary-20 hover:bg-primary-50 rounded-lg"
                    label={
                      <p className="w-full text-center text-sm font-medium text-primary-500">
                        Create template
                      </p>
                    }
                    name="create-template"
                    onChange={() => {}}
                    className="flex min-h-[76px] w-[176px] flex-col items-center  justify-center gap-2 rounded-lg border-2 border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50"
                  />
                </div>
              </Link>
            )}
          </Show>
          {renderedTemplates.map((template) => (
            <CheckboxCard
              disabled={
                useGroupTemplate ||
                (readOnly && template.id !== selectedTemplateId)
              }
              key={template.id}
              checked={selectedTemplateId === template.id && !useGroupTemplate}
              id={`${instanceId}-${template.id}`}
              wrapperClassName="border-alpha-100 animate-appear"
              label={
                <div className="relative w-full">
                  <Show if={template.default}>
                    <p className="absolute -right-4 -top-4 rounded-bl-lg rounded-tr-xl border-b border-l border-alpha-100 bg-white px-2 text-xs font-medium">
                      Default
                    </p>
                  </Show>
                  <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium text-content-600">
                    {template.name}
                  </p>
                  <Button
                    variant={ButtonVariant.TEXT}
                    disabled={useGroupTemplate}
                    onClick={() => setReviewModalId(template.id)}
                    className="text-xs"
                  >
                    Review
                  </Button>
                </div>
              }
              name="name"
              onChange={() => onTemplateSelect?.(template.id)}
              className="flex max-h-[76px] w-full justify-start"
            />
          ))}
          <Hide if={(templates?.length || 0) < 7}>
            <div className="col-span-4 flex justify-center w-full">
              <Button
                Icon={showMoreSettings ? ChevronUp : ChevronDown}
                borderLess
                variant={ButtonVariant.BASE}
                onClick={() => setShowMoreSettings((prev) => !prev)}
              >
                {showMoreSettings ? "Show less" : "Show more"}
              </Button>
            </div>
          </Hide>
        </div>
      </div>
      {reviewModalId ? (
        <TemplateReviewModal
          opened={Boolean(reviewModalId)}
          onClose={() => setReviewModalId(null)}
          id={reviewModalId}
        />
      ) : null}
    </SectionCard>
  );
};
